import {
  Dispatch,
  SetStateAction,
  CSSProperties,
  RefObject,
  createContext,
} from "react";
import { Placement } from "@floating-ui/react-dom";

import { TooltipProps } from "../Tooltip";

/**
 * Context props for the Tooltip component
 */
export type TooltipContextProps = {
  /**
   * Unique identifier for the tooltip
   */
  tooltipId: string;
  /**
   * Function to set the tooltip ID
   */
  setTooltipId: Dispatch<SetStateAction<string>>;
  /**
   * The trigger element reference
   */
  invoker?: HTMLElement;
  /**
   * Function to set the trigger element
   */
  setInvoker?: Dispatch<SetStateAction<HTMLElement | undefined>>;
  /**
   * The tooltip content element reference
   */
  popover?: HTMLElement;
  /**
   * Function to set the tooltip content element
   */
  setTooltip?: Dispatch<SetStateAction<HTMLElement | undefined>>;
  /**
   * The root node for the tooltip
   */
  rootNode?: HTMLElement;
  /**
   * Function to set the root node
   */
  setRootNode?: Dispatch<SetStateAction<HTMLElement | undefined>>;
  /**
   * The arrow element reference
   */
  arrowElement?: HTMLSpanElement;
  /**
   * Function to set the arrow element
   */
  setArrowElement?: Dispatch<SetStateAction<HTMLSpanElement | undefined>>;
  /**
   * Current open state of the tooltip
   */
  openState: boolean;
  /**
   * Function to set the open state
   */
  setOpenState: Dispatch<SetStateAction<boolean>>;
  /**
   * Inline styles for the tooltip
   */
  tooltipStyle?: CSSProperties;
  /**
   * Placement of the tooltip
   */
  placement?: Placement;
  /**
   * Function to open the tooltip
   */
  openTooltip: () => void;
  /**
   * Function to close the tooltip
   */
  closeTooltip: (replacementFn?: () => void) => void;
  /**
   * Whether the tooltip is controlled
   */
  controlled: boolean;
  /**
   * Whether to disable flip fallback
   */
  disableFlipFallback: TooltipProps["disableFlipFallback"];
  /**
   * Reference to the arrow element
   */
  arrowRef: RefObject<HTMLSpanElement>;
  /**
   * Key for resetting the tooltip
   */
  resetKey: number;
};

/**
 * React context for sharing tooltip state and functions between components
 */
export const TooltipContext = createContext<TooltipContextProps | null>(null);
